import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Header from "../../../components/Header";
import usePermissions from "../../../data/RBAC/PermissionsHook";
import { tokens } from "../../../theme";
import { fetchAllDashboardMessages } from "../../../data/DashboardNotifications/dashboardMessagesApi";
import DashboardMessageForm from "./forms";
import {
  getCategoryGradient,
  getCategoryMeta,
} from "../../../data/DashboardNotifications/notificationStyles";

// DynamicTableForJson can't be used here — its fetch layer always requests
// a { results, error } JSON-string envelope, but the dashboard_messages
// resolvers return the typed object/list directly. See dashboardMessagesApi.js.
const DashboardMessagesTable = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { hasPermission } = usePermissions();
  const entity = localStorage.getItem("SelectedRight");

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeRow, setActiveRow] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const results = await fetchAllDashboardMessages();
      setRows(results);
      setError(null);
    } catch (err) {
      console.error("Failed to load dashboard messages:", err);
      setError("Unable to load dashboard messages. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = () => {
    setIsEditing(false);
    setEditData(null);
    setDrawerOpen(true);
  };

  const handleEditFromMenu = () => {
    setIsEditing(true);
    setEditData(activeRow);
    setDrawerOpen(true);
    setMenuAnchorEl(null);
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setActiveRow(row);
  };

  const columns = [
    { field: "title", headerName: "Title", flex: 1.4, minWidth: 160 },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      minWidth: 140,
      renderCell: ({ value }) => {
        const meta = getCategoryMeta(value);
        return (
          <Chip
            size="small"
            icon={<meta.Icon style={{ fontSize: 15, color: "#fff" }} />}
            label={meta.label}
            sx={{
              fontWeight: 700,
              fontSize: "0.72rem",
              color: "#fff",
              background: getCategoryGradient(meta, isDark),
              boxShadow: `0 2px 6px ${meta.accent}55`,
              "& .MuiChip-icon": { color: "#fff" },
            }}
          />
        );
      },
    },
    { field: "displayType", headerName: "Display", flex: 0.8, minWidth: 100 },
    { field: "priority", headerName: "Priority", flex: 0.6, minWidth: 90 },
    {
      field: "isDismissible",
      headerName: "Dismissible",
      flex: 0.7,
      minWidth: 100,
      renderCell: ({ value }) => (value ? "Yes" : "No"),
    },
    {
      field: "startsAt",
      headerName: "Starts",
      flex: 1,
      minWidth: 140,
      renderCell: ({ value }) => (value ? new Date(value).toLocaleString() : "—"),
    },
    {
      field: "expiresAt",
      headerName: "Expires",
      flex: 1,
      minWidth: 140,
      renderCell: ({ value }) => (value ? new Date(value).toLocaleString() : "Never"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Header
        title="Dashboard Messages"
        subtitle="Publish banners, modals, cards and toasts to the user dashboard"
        action={
          hasPermission(entity, "CreatePermission") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                bgcolor: colors.primary[500],
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: colors.primary[500], opacity: 0.9 },
              }}
            >
              Add
            </Button>
          )
        }
      />

      {error ? (
        <Box
          sx={{
            p: 3,
            borderRadius: "10px",
            border: `1px solid ${colors.redAccent[500]}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography color={colors.redAccent[400]}>{error}</Typography>
          <Button onClick={loadData} variant="outlined" color="error">
            Retry
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            height: 600,
            "& .MuiDataGrid-root": {
              border: "none",
              bgcolor: isDark ? "#162736" : "#ffffff",
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
          />
        </Box>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        {hasPermission(entity, "EditPermission") && (
          <MenuItem onClick={handleEditFromMenu}>Edit</MenuItem>
        )}
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: isMobile ? "100vw" : 600 }}>
          <DashboardMessageForm
            isEditing={isEditing}
            data={editData}
            onClose={() => setDrawerOpen(false)}
            onAction={() => {
              setDrawerOpen(false);
              loadData();
            }}
          />
        </Box>
      </Drawer>
    </Box>
  );
};

export default DashboardMessagesTable;
